import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageTitle from "../../layouts/PageTitle";
import {
  getAdminRevenueApi,
  getAdminEarningStatsApi,
} from "../../../services/EarningsService";

const formatDate = (v) => {
  if (!v) return "-";
  return new Date(v).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatCard = ({ label, value, sub, icon, color }) => (
  <div className="col-xl-3 col-sm-6">
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-center">
          <div
            className={`rounded-circle d-flex align-items-center justify-content-center bg-${color} text-white me-3`}
            style={{ width: 52, height: 52, fontSize: 20, flexShrink: 0 }}
          >
            <i className={`fa ${icon}`} />
          </div>
          <div>
            <div className="text-muted small mb-1">{label}</div>
            <div className="fs-4 fw-bold">{value}</div>
            {sub && <div className="text-muted small">{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const RevenueCollection = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState(20);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    sessionType: "",
    dateFrom: null,
    dateTo: null,
  });
  const [applied, setApplied] = useState({});

  const fetchRevenue = async (params) => {
    try {
      setLoading(true);
      const res = await getAdminRevenueApi(params);
      const d = res?.data;
      setRows(Array.isArray(d?.data) ? d.data : []);
      setTotal(Number(d?.pagination?.total) || 0);
      setTotalPages(Number(d?.pagination?.totalPages) || 1);
    } catch {
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenue({ page, limit: sort, ...applied });
  }, [page, sort, applied]);

  useEffect(() => {
    getAdminEarningStatsApi()
      .then((res) => setStats(res?.data?.data || null))
      .catch(() => {});
  }, []);

  const handleApply = () => {
    setPage(1);
    setApplied({
      ...filters,
      dateFrom: filters.dateFrom ? filters.dateFrom.toISOString().split("T")[0] : "",
      dateTo: filters.dateTo ? filters.dateTo.toISOString().split("T")[0] : "",
    });
  };
  const handleReset = () => {
    const empty = { search: "", sessionType: "", dateFrom: null, dateTo: null };
    setFilters(empty);
    setApplied({});
    setPage(1);
  };

  const paggination = Array(totalPages)
    .fill(null)
    .map((_, i) => i + 1);
  const showingFrom = total > 0 ? (page - 1) * sort + 1 : 0;
  const showingTo = total > 0 ? Math.min(page * sort, total) : 0;

  // Total revenue shown in table (current filtered page won't represent grand total — use stats for that)
  const pageRevenue = rows.reduce(
    (acc, r) => acc + Number(r.totalRevenue || 0),
    0,
  );

  return (
    <>
      <PageTitle activeMenu="Revenue Collection" motherMenu="Revenue" />

      {/* ── Stat Cards ── */}
      <div className="row mb-4">
        <StatCard
          label="Total Gross Revenue"
          value={
            stats
              ? `₹${Number(stats.totalGrossRevenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
              : "—"
          }
          sub={stats ? `${stats.totalSessionsBilled || 0} sessions billed` : ""}
          icon="fa-inr"
          color="primary"
        />
        <StatCard
          label="Platform Commission"
          value={
            stats
              ? `₹${Number(stats.totalCommissionCollected || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
              : "—"
          }
          sub={stats ? `${stats.currentCommissionPercentage || 0}% rate` : ""}
          icon="fa-percent"
          color="success"
        />
        <StatCard
          label="Paid to Consultants"
          value={
            stats
              ? `₹${Number(stats.totalPaidToConsultants || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
              : "—"
          }
          icon="fa-users"
          color="info"
        />
        <StatCard
          label="Pending Withdrawals"
          value={
            stats
              ? `₹${Number(stats.pendingWithdrawals?.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
              : "—"
          }
          sub={stats ? `${stats.pendingWithdrawals?.count || 0} requests` : ""}
          icon="fa-clock-o"
          color="warning"
        />
      </div>

      {/* ── Filters ── */}
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="card-title mb-0">Search & Filter</h4>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search by customer name, email or phone"
                value={filters.search}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, search: e.target.value }))
                }
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={filters.sessionType}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, sessionType: e.target.value }))
                }
              >
                <option value="">All Session Types</option>
                <option value="call">Call</option>
                <option value="chat">Chat</option>
              </select>
            </div>
            <div className="col-md-2">
              <DatePicker
                selected={filters.dateFrom}
                onChange={(date) => setFilters((p) => ({ ...p, dateFrom: date }))}
                selectsStart
                startDate={filters.dateFrom}
                endDate={filters.dateTo}
                maxDate={filters.dateTo || new Date()}
                placeholderText="From date"
                dateFormat="dd/MM/yyyy"
                className="form-control"
                wrapperClassName="w-100"
                isClearable
              />
            </div>
            <div className="col-md-2">
              <DatePicker
                selected={filters.dateTo}
                onChange={(date) => setFilters((p) => ({ ...p, dateTo: date }))}
                selectsEnd
                startDate={filters.dateFrom}
                endDate={filters.dateTo}
                minDate={filters.dateFrom}
                maxDate={new Date()}
                placeholderText="To date"
                dateFormat="dd/MM/yyyy"
                className="form-control"
                wrapperClassName="w-100"
                isClearable
              />
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={handleApply}>
              Apply
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── Revenue Table ── */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="card-title mb-0">Customer-wise Revenue</h4>
            {rows.length > 0 && (
              <div className="text-muted small mt-1">
                Page total:{" "}
                <strong className="text-primary">
                  ₹{pageRevenue.toFixed(2)}
                </strong>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Show</span>
            <Dropdown>
              <Dropdown.Toggle
                as="div"
                className="search-drop-btn"
                style={{ cursor: "pointer" }}
              >
                {sort}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {[10, 20, 30, 50].map((n) => (
                  <Dropdown.Item
                    key={n}
                    onClick={() => {
                      setSort(n);
                      setPage(1);
                    }}
                  >
                    {n}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <span className="text-muted small">entries</span>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Total Sessions</th>
                  <th>Call / Chat</th>
                  <th>Total Duration</th>
                  <th>Total Spent</th>
                  <th>First Session</th>
                  <th>Last Session</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-muted">
                      Loading revenue data...
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-muted">
                      No revenue data found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((r, i) => (
                    <tr key={r.customerId || i}>
                      <td className="text-muted small">{showingFrom + i}</td>
                      <td>
                        <div
                          className="fw-semibold"
                          style={{ textTransform: "capitalize" }}
                        >
                          {r.customerName}
                        </div>
                        <div className="text-muted small">
                          {r.customerEmail}
                        </div>
                      </td>
                      <td className="text-muted small">
                        {r.customerPhone || "-"}
                      </td>
                      <td className="text-center">
                        <span className="badge badge-primary light">
                          {r.totalSessions}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge badge-primary dark me-1">
                          <i className="fa fa-phone me-1" />
                          {r.callSessions}
                        </span>
                        <span className="badge badge-secondary light">
                          <i className="fa fa-comments me-1" />
                          {r.chatSessions}
                        </span>
                      </td>
                      <td>
                        {r.totalDurationMinutes > 0
                          ? `${r.totalDurationMinutes} min`
                          : "-"}
                      </td>
                      <td>
                        <span
                          className="fw-bold text-primary"
                          style={{ fontSize: 15 }}
                        >
                          ₹
                          {Number(r.totalRevenue).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </td>
                      <td className="text-muted small">
                        {formatDate(r.firstSessionAt)}
                      </td>
                      <td className="text-muted small">
                        {formatDate(r.lastSessionAt)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-sm-flex text-center justify-content-between align-items-center px-3 py-3">
            <div className="dataTables_info">
              Showing {showingFrom} to {showingTo} of {total} customers
            </div>
            <div className="dataTables_paginate paging_simple_numbers">
              <Link
                to="#"
                className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                onClick={() => page > 1 && setPage(page - 1)}
              >
                Previous
              </Link>
              <span>
                {paggination.map((n) => (
                  <Link
                    key={n}
                    to="#"
                    className={`paginate_button ${page === n ? "current" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </Link>
                ))}
              </span>
              <Link
                to="#"
                className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                onClick={() => page < totalPages && setPage(page + 1)}
              >
                Next
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RevenueCollection;
